<template>
  <div class="slu" :class="{ 'slu--unlocked': isUnlocked, 'slu--disabled': disabled }">
    <div
      ref="track"
      class="slu-track"
      @mousedown.prevent="startDrag"
      @touchstart.prevent="startDrag"
    >
      <div
        class="slu-fill"
        :style="{ width: isUnlocked ? '100%' : fillPercent + '%' }"
        :class="{ 'slu-fill--snap': isUnlocked || snapping }"
      ></div>

      <span v-if="!isUnlocked" class="slu-label" :style="{ opacity: labelOpacity }">
        <v-icon small class="mr-1" style="color: #4a5568">mdi-lock-outline</v-icon>
        {{ $t('Arraste para desbloquear') }}
      </span>
      <span v-else class="slu-label slu-label--unlocked">
        <v-icon small class="mr-1" color="white">mdi-lock-open-variant-outline</v-icon>
        {{ $t('Desbloqueado') }}
      </span>

      <div
        class="slu-handle"
        :style="{ transform: `translateX(${handleX}px)` }"
        :class="{ 'slu-handle--snap': isUnlocked || snapping }"
      >
        <v-icon color="white" size="20">
          {{ isUnlocked ? 'mdi-lock-open-variant-outline' : 'mdi-chevron-double-right' }}
        </v-icon>
      </div>
    </div>
  </div>
</template>

<script>
const HANDLE_SIZE = 46

export default {
  name: 'SlideToUnlock',

  props: {
    locked: { type: Boolean, default: true },
    disabled: { type: Boolean, default: false },
  },

  data: () => ({
    dragging: false,
    handleX: 0,
    maxX: 0,
    startClientX: 0,
    isUnlocked: false,
    snapping: false,
  }),

  computed: {
    fillPercent() {
      if (!this.maxX) return 0
      return (this.handleX / this.maxX) * 100
    },
    labelOpacity() {
      return Math.max(0, 1 - this.fillPercent / 40)
    },
  },

  watch: {
    locked(val) {
      if (val && this.isUnlocked) {
        this.reset()
      }
    },
  },

  methods: {
    startDrag(e) {
      if (this.disabled || this.isUnlocked) return
      const rect = this.$refs.track.getBoundingClientRect()
      this.maxX = rect.width - HANDLE_SIZE
      this.startClientX = e.type === 'touchstart' ? e.touches[0].clientX : e.clientX
      this.dragging = true
      document.addEventListener('mousemove', this.onDrag)
      document.addEventListener('mouseup', this.endDrag)
      document.addEventListener('touchmove', this.onDrag, { passive: false })
      document.addEventListener('touchend', this.endDrag)
    },

    onDrag(e) {
      if (!this.dragging) return
      if (e.cancelable) e.preventDefault()
      const clientX = e.type === 'touchmove' ? e.touches[0].clientX : e.clientX
      const delta = clientX - this.startClientX
      this.handleX = Math.min(Math.max(0, delta), this.maxX)
    },

    endDrag() {
      if (!this.dragging) return
      this.dragging = false
      document.removeEventListener('mousemove', this.onDrag)
      document.removeEventListener('mouseup', this.endDrag)
      document.removeEventListener('touchmove', this.onDrag)
      document.removeEventListener('touchend', this.endDrag)

      if (this.handleX >= this.maxX * 0.88) {
        this.snapping = true
        this.handleX = this.maxX
        setTimeout(() => {
          this.isUnlocked = true
          this.snapping = false
          this.$emit('unlock')
        }, 200)
      } else {
        this.snapping = true
        this.handleX = 0
        setTimeout(() => { this.snapping = false }, 300)
      }
    },

    reset() {
      this.snapping = true
      this.handleX = 0
      this.isUnlocked = false
      setTimeout(() => { this.snapping = false }, 300)
    },
  },
}
</script>

<style scoped lang="scss">
.slu {
  display: inline-block;
  width: 260px;
  flex-shrink: 0;

  &--disabled {
    opacity: 0.5;
    pointer-events: none;
  }
}

.slu-track {
  position: relative;
  height: 46px;
  background: #dde2e8;
  border-radius: 23px;
  overflow: hidden;
  cursor: grab;
  user-select: none;

  &:active {
    cursor: grabbing;
  }

  .slu--unlocked & {
    cursor: default;
  }
}

.slu-fill {
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  background: #bbcf20;
  border-radius: 23px;
  pointer-events: none;

  &--snap {
    transition: width 0.25s ease;
  }

  .slu--unlocked & {
    background: #2A8EA4;
  }
}

.slu-label {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  white-space: nowrap;
  font-size: 0.75rem;
  font-weight: 600;
  color: #4a5568;
  pointer-events: none;
  z-index: 1;
  display: flex;
  align-items: center;

  &--unlocked {
    color: white;
    opacity: 1 !important;
  }
}

.slu-handle {
  position: absolute;
  top: 0;
  left: 0;
  width: 46px;
  height: 46px;
  background: #0f3565;
  border-radius: 23px;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.25);
  pointer-events: none;

  &--snap {
    transition: transform 0.25s ease;
  }

  .slu--unlocked & {
    background: #2A8EA4;
  }
}
</style>
